'use client';

import {
  CheckCircle2,
  FileText,
  Loader2,
  Pencil,
  RotateCcw,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Badge } from '@/components/ui/Badge';
import { PlanUpsell, useCapability } from '@/features/billing';
import { ManualActionBar } from '@/features/workflows/components/ManualActionBar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { getConfig } from '@/lib/config';
import { useFeature } from '@/lib/featureFlags';
import { cn } from '@/lib/utils';
import { formatCreditCost } from '@/features/usage/format';
import { useLocale } from '@/lib/i18n/useLocale';
import { friendlyMessage } from '@/lib/errors/catalog';
import { HttpError } from '@/lib/errors/http';
import type { Recording, TranscriptSegment } from '@/lib/protobuf/gen/recordings/recordings_pb';
import { useNotesStore } from '@/features/notes/notesStore';
import { useAuthStore } from '@/stores/authStore';
import { fetchAudioBlobURL, fetchTranscript, type TranscriptResponse, type UpdateRecordingFields } from './api';
import { failureReasonLabel } from './failureReason';
import { Highlight } from './Highlight';
import { canCancelTranscription, STATUS_CANCELLED, useRecordingsStore } from './recordingsStore';
import { showsSpinner, statusBadgeLabel } from './statusLabels';

function formatElapsed(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = Math.floor(totalSec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function progressLabel(status: string, t: ReturnType<typeof useTranslations>): string {
  switch (status) {
    case 'uploading':
      return t('transcribingProgress.uploading');
    case 'pending':
    case 'queued':
      return t('transcribingProgress.queued');
    default:
      return t('transcribingProgress.transcribing');
  }
}

/**
 * In-progress indicator for a recording that's still being transcribed.
 * ASR has no token-level progress to report, so the honest "progress" signal
 * is elapsed time + a reassurance that long audio legitimately takes minutes
 * — which keeps the user from reading the wait as a stall. Ticks once a
 * second off `recording.createdAt`.
 */
function TranscribingProgress({ recording }: { recording: Recording }) {
  const t = useTranslations('recordings');
  const startMs = recording.createdAt ? Number(recording.createdAt.seconds) * 1000 : null;
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsedSec = startMs !== null ? Math.max(0, (now - startMs) / 1000) : null;
  let limitMin = 30;
  try {
    const secs = getConfig().asrMaxWaitSecs;
    if (typeof secs === 'number' && secs > 0) limitMin = Math.round(secs / 60);
  } catch {
    // config not ready — fall back to the documented 30-min default.
  }
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
      <span>
        {progressLabel(recording.status, t)}
        {elapsedSec !== null &&
          t('transcribingProgress.elapsed', { time: formatElapsed(elapsedSec) })}
        {t('transcribingProgress.limitHint', { limit: limitMin })}
      </span>
    </div>
  );
}

function formatDuration(secs: number): string {
  if (!Number.isFinite(secs) || secs <= 0) return '0:00';
  const total = Math.floor(secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
}

function formatTimestamp(secs: number): string {
  return formatDuration(secs);
}

/**
 * Binary search over `segments` (assumed start-sorted) for the segment
 * containing `t`. Returns `-1` when before the first segment or no match.
 */
export function findSegmentIndex(segments: readonly TranscriptSegment[], t: number): number {
  if (segments.length === 0) return -1;
  if (t < segments[0]!.startSecs) return -1;
  let lo = 0;
  let hi = segments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const seg = segments[mid]!;
    if (t < seg.startSecs) {
      hi = mid - 1;
    } else if (t > seg.endSecs) {
      lo = mid + 1;
    } else {
      return mid;
    }
  }
  // No exact hit — return the last segment whose start ≤ t (so we
  // highlight the most recent turn even between two segments).
  return Math.min(Math.max(0, hi), segments.length - 1);
}

/**
 * The subtle accent-tinted "you · auto" pill shown next to a speaker that the
 * voiceprint match identified as the account holder. Kept tiny and low-contrast
 * — it's a confidence cue, not a call to action. The "auto" half signals the
 * label came from the automatic match (vs. a manual "This is me"), which is
 * exactly the distinction the active-learning control lets the user correct.
 */
function SelfBadge() {
  const t = useTranslations('recordings');
  return (
    <span
      className="ml-1 inline-flex items-center rounded-full bg-accent/10 px-1.5 py-0 text-[10px] font-medium leading-[1.4] text-accent"
      title={t('selfBadge.title')}
      data-testid="self-speaker-badge"
    >
      {t('selfBadge.label')}
    </span>
  );
}

/**
 * The speaker-label chips: a plain-text rename affordance per cluster, plus the
 * self-recognition layer (when its feature is on). The self cluster renders the
 * account holder's login display name + a "you · auto" badge instead of the raw
 * label and exposes a "Not me" correction; every other cluster keeps the
 * existing pencil-rename and (when self-recognition is on) a "This is me"
 * control. Extracted from `RecordingPlayer` to keep the player's render under
 * the cyclomatic-complexity budget and to localise the editing state.
 */
function SpeakerChips({
  speakers,
  recordingId,
  rename,
  selfLabel,
  displayName,
  selfControlsEnabled,
  setSelfSpeaker,
}: {
  speakers: readonly string[];
  recordingId: string;
  rename: (id: string, fields: UpdateRecordingFields) => void;
  /** Cluster label resolved to the account holder, or "" when none. */
  selfLabel: string;
  /** The account holder's login display name (rendered for the self chip). */
  displayName: string;
  /** Whether the "This is me / Not me" controls should render (flag + ready). */
  selfControlsEnabled: boolean;
  setSelfSpeaker: (id: string, label: string, isSelf: boolean) => Promise<void>;
}) {
  const t = useTranslations('recordings');
  const [editingSpeaker, setEditingSpeaker] = React.useState<{ idx: number; value: string } | null>(null);

  if (speakers.length === 0) return null;

  const commitSpeakerRename = () => {
    if (!editingSpeaker) return;
    const next = [...speakers];
    next[editingSpeaker.idx] = editingSpeaker.value.trim() || next[editingSpeaker.idx]!;
    setEditingSpeaker(null);
    // Only the speakers field — never sends title or collection_id.
    rename(recordingId, { speakers: next });
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="speaker-chips">
      {speakers.map((sp, idx) => {
        const isSelf = !!selfLabel && sp === selfLabel;
        const isEditing = editingSpeaker?.idx === idx;
        return (
          <span
            key={`${sp}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-0.5 text-xs text-foreground"
          >
            {isEditing ? (
              <Input
                autoFocus
                value={editingSpeaker.value}
                onChange={(e) => setEditingSpeaker({ idx, value: e.target.value })}
                onBlur={commitSpeakerRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitSpeakerRename();
                  } else if (e.key === 'Escape') {
                    setEditingSpeaker(null);
                  }
                }}
                aria-label={t('speakerChips.renameSpeakerLabel', { speaker: sp })}
                className="h-5 px-1 text-xs w-32"
              />
            ) : isSelf ? (
              // Self-matched cluster: render the login display name + badge. No
              // plain-text rename pencil — the identity is the account holder's
              // name, corrected via "Not me", not by typing.
              <span className="inline-flex items-center">
                <span>{displayName}</span>
                <SelfBadge />
              </span>
            ) : (
              <>
                <span>{sp}</span>
                <button
                  type="button"
                  onClick={() => setEditingSpeaker({ idx, value: sp })}
                  className="text-muted-foreground hover:text-foreground transition-colors duration-150"
                  aria-label={t('speakerChips.renameLabel', { speaker: sp })}
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </>
            )}
            {/* Active-learning control (one per cluster). Hidden while editing
                this chip and unless self-recognition is on + the recording is
                ready with clusters to correct. */}
            {selfControlsEnabled && !isEditing && (
              <Button
                variant="ghost"
                size="sm"
                className="h-4 px-1 text-[10px] text-muted-foreground hover:text-foreground"
                onClick={() => setSelfSpeaker(recordingId, sp, !isSelf)}
                aria-label={
                  isSelf
                    ? t('speakerChips.notMeLabel', { speaker: sp })
                    : t('speakerChips.thisIsMeLabel', { speaker: sp })
                }
                data-testid={`self-toggle-${idx}`}
              >
                {isSelf ? t('speakerChips.notMe') : t('speakerChips.thisIsMe')}
              </Button>
            )}
          </span>
        );
      })}
    </div>
  );
}

/**
 * The audio element + its load/error/idle states. Extracted from the player so
 * the player render stays under the complexity budget; purely presentational
 * (the blob-URL fetch + revoke lifecycle stays in the player, which owns the
 * AbortController and the audioRef the transcript seek/timeupdate needs).
 */
function AudioSection({
  audioRef,
  audioState,
  audioError,
  audioSrc,
  status,
  title,
  mime,
  onTimeUpdate,
}: {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  audioState: 'idle' | 'loading' | 'ready' | 'error';
  audioError: string | null;
  audioSrc: string | null;
  status: string;
  title?: string;
  mime?: string;
  onTimeUpdate: () => void;
}) {
  const t = useTranslations('recordings');
  return (
    <div className="space-y-1">
      {audioState === 'loading' && (
        <p className="text-xs text-muted-foreground">{t('audioSection.loading')}</p>
      )}
      {audioState === 'error' && (
        <p className="text-sm text-destructive">
          {t('audioSection.unavailable', {
            error: audioError ?? t('audioSection.unknownError'),
            status,
          })}
        </p>
      )}
      {audioState === 'idle' && (
        <p className="text-xs text-muted-foreground">
          {t('audioSection.idle', { status })}
        </p>
      )}
      {audioSrc && (
        <audio
          ref={audioRef}
          controls
          preload="metadata"
          className="w-full"
          src={audioSrc}
          onTimeUpdate={onTimeUpdate}
        >
          <track kind="captions" />
        </audio>
      )}
      {audioSrc && (
        // Download reuses the SAME blob URL the player already holds — the raw
        // endpoint can't go in href because the browser would fetch it without
        // our Bearer header and get a 401 (same reason as `src` above). So this
        // costs no extra request and works whenever the player does.
        <a
          href={audioSrc}
          download={downloadName(title, mime)}
          className="inline-block text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {t('audioSection.download')}
        </a>
      )}
    </div>
  );
}

/**
 * Filename for the download affordance: the user's title, sanitised for a
 * filesystem, plus an extension derived from the stored MIME. Mirrors the
 * server's pickExt (internal/recordings/ingest.go) so a downloaded file opens
 * in the player the user expects.
 */
function downloadName(title: string | undefined, mime: string | undefined): string {
  const base =
    (title ?? '')
      .replace(/[^\p{L}\p{N} ._-]/gu, '')
      .trim()
      .slice(0, 80) || 'recording';
  const ext =
    {
      'audio/mpeg': 'mp3',
      'audio/wav': 'wav',
      'audio/mp4': 'm4a',
      'audio/ogg': 'ogg',
      'audio/flac': 'flac',
      'video/mp4': 'mp4',
      'video/webm': 'webm',
    }[mime ?? ''] ?? 'bin';
  return `${base}.${ext}`;
}

/**
 * A failed recording's reason (BILL-05): machine sentinels render as catalog
 * copy via `failureReasonLabel`, anything else verbatim, an empty reason as
 * `fallback`. `withTopUp` appends the "Add credits" link to /billing for the
 * credits reason.
 */
function FailedReasonText({
  reason,
  fallback,
  withTopUp = false,
}: {
  reason: string;
  fallback: string;
  withTopUp?: boolean;
}) {
  const t = useTranslations('recordings');
  const failure = failureReasonLabel(reason, (k) => t(k as never));
  if (!failure) return <>{fallback}</>;
  return (
    <>
      {failure.text}
      {withTopUp && failure.topUp && (
        <>
          {' '}
          <Link
            href="/billing"
            className="inline-flex items-center rounded-sm font-medium text-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('failure.topUp')}
          </Link>
        </>
      )}
    </>
  );
}

interface RecordingPlayerProps {
  recording: Recording;
  onClose?: () => void;
  /** Active library search term — highlights matching transcript turns. */
  query?: string | undefined;
}

/**
 * BILL-20f: the one inline upsell under the action row when Retry /
 * Re-transcribe are locked for the plan. Its own component to keep
 * RecordingPlayer under the complexity cap.
 */
function TranscriptionPlanUpsell({ show, requiredTier }: { show: boolean; requiredTier: string }) {
  if (!show) return null;
  // New tab: a held capture lives in this tab's memory, and checkout redirects the tab.
  return <PlanUpsell variant="inline" feature="recordings_asr" requiredTier={requiredTier || undefined} newTab />;
}

function RecordingPlayer({ recording, onClose, query }: RecordingPlayerProps) {
  const t = useTranslations('recordings');
  const locale = useLocale();
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const activeIdxRef = React.useRef<number>(-1);

  const [activeIdx, setActiveIdx] = React.useState<number>(-1);
  const [transcript, setTranscript] = React.useState<TranscriptResponse | null>(null);
  const [transcriptError, setTranscriptError] = React.useState<string | null>(null);

  const rename = useRecordingsStore((s) => s.actions.rename);
  const retry = useRecordingsStore((s) => s.actions.retry);
  // BILL-20f: below Standard the server refuses RetryTranscription (241);
  // `unknown` never locks — the server stays the boundary.
  const asr = useCapability('recordings_asr');
  const asrLocked = asr.entitlement === 'locked';
  const cancelTranscription = useRecordingsStore((s) => s.actions.cancelTranscription);
  const cancelPending = useRecordingsStore((s) => s.cancelPending);
  const destroy = useRecordingsStore((s) => s.actions.destroy);
  const promote = useRecordingsStore((s) => s.actions.promote);
  const setSelfSpeaker = useRecordingsStore((s) => s.actions.setSelfSpeaker);

  // R22 agentic ambient capture: per-recording opt-in to auto-act on the
  // transcript. Gated behind the agentic_capture feature flag.
  const agenticCaptureEnabled = useFeature('agentic_capture');

  // Speaker-recognition (self-only voiceprint). When on, the cluster matched as
  // the account holder is badged with their login display name + "you · auto",
  // and a per-cluster "This is me / Not me" control lets them correct the match
  // (active learning). Flag OFF ⇒ none of this renders and the player is
  // byte-identical to today.
  const speakerRecognitionEnabled = useFeature('speaker_recognition');
  // The diarized cluster label resolved to self (e.g. "Speaker 2"), or "" when
  // no match / feature off. Empty string must never count as a match — an empty
  // `seg.speaker` would otherwise badge every untagged turn as "you".
  const selfLabel = recording.selfSpeakerLabel || '';
  // Resolved at render time from the live login/profile display name so it's
  // always current (the name is never persisted on the voiceprint — see design).
  const displayName = useAuthStore((s) => s.user?.displayName) ?? t('recordingPlayer.you');

  // Load transcript when status reaches a terminal state with a transcript.
  React.useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    setTranscript(null);
    setTranscriptError(null);
    if (!recording.recordingId) return;
    if (recording.status !== 'ready' && recording.status !== 'completed') return;

    void (async () => {
      try {
        const t = await fetchTranscript(recording.recordingId, ac.signal);
        if (!cancelled) setTranscript(t);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // R7: show the warm/safe catalog message, never a raw error string.
        setTranscriptError(
          err instanceof HttpError ? friendlyMessage(err.wire.code, err.wire.message) : friendlyMessage(undefined),
        );
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [recording.recordingId, recording.status]);

  // Prefer the live recording's embedded transcript over the HTTP-fetched
  // snapshot. After an UpdateRecording (e.g. speaker rename) the WS response
  // upserts a fresh recording with rewritten segments; the local `transcript`
  // state is the prior snapshot from a one-shot fetchTranscript() at mount
  // and would otherwise shadow the rename until reload. The HTTP fetch
  // remains the backstop for the edge case where the WS payload doesn't
  // include a transcript yet.
  const segments: TranscriptSegment[] = React.useMemo(
    () => recording.transcript?.segments ?? transcript?.transcript?.segments ?? [],
    [transcript, recording.transcript],
  );

  // Fetch the audio bytes with the Authorization header injected and hand the
  // <audio> element a blob URL. We can't put the bare audio endpoint into
  // `src` because the browser fetches it without our Bearer header and the
  // backend returns 401. The blob URL is revoked on unmount / recording
  // switch so the bytes don't leak across mounts.
  type AudioState = 'idle' | 'loading' | 'ready' | 'error';
  const [audioSrc, setAudioSrc] = React.useState<string | null>(null);
  const [audioError, setAudioError] = React.useState<string | null>(null);
  const [audioState, setAudioState] = React.useState<AudioState>('idle');
  React.useEffect(() => {
    setAudioError(null);
    setAudioState('idle');
    if (!recording.recordingId) return;
    // Audio exists from the moment ingest stores the blob. Transcription is an
    // INDEPENDENT downstream step: a failed or in-flight transcription says
    // nothing about whether the bytes are playable, and the server never gated
    // on status either — http_handler.go reads the blob straight off
    // rec.BlobRef with no status check. Gating HERE is what stranded a user
    // with a recording they could neither play nor download because an
    // unrelated ASR step had failed (prod 2026-08-05). Skip only while the
    // bytes genuinely cannot exist yet: the client-side pending-upload states.
    if (recording.status === 'uploading' || recording.status === 'pending') return;
    const ac = new AbortController();
    let revokedURL: string | null = null;
    setAudioState('loading');
    void (async () => {
      try {
        const url = await fetchAudioBlobURL(recording.recordingId, ac.signal);
        revokedURL = url;
        setAudioSrc(url);
        setAudioState('ready');
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setAudioError(
          err instanceof HttpError ? friendlyMessage(err.wire.code, err.wire.message) : friendlyMessage(undefined),
        );
        setAudioState('error');
      }
    })();
    return () => {
      ac.abort();
      if (revokedURL) URL.revokeObjectURL(revokedURL);
      setAudioSrc(null);
    };
  }, [recording.recordingId, recording.status]);

  const onTimeUpdate = React.useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const idx = findSegmentIndex(segments, audio.currentTime);
    if (idx !== activeIdxRef.current) {
      activeIdxRef.current = idx;
      setActiveIdx(idx);
    }
  }, [segments]);

  // Seek when a turn is clicked.
  const seekTo = (secs: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = secs;
    if (audio.paused) void audio.play().catch(() => undefined);
  };

  // The distinct speaker labels, used by the chips block (extracted into
  // `SpeakerChips`). Prefer the server's `speakers` list; fall back to the
  // distinct labels present in the segments.
  const speakers = recording.speakers && recording.speakers.length > 0
    ? recording.speakers
    : Array.from(new Set(segments.map((s) => s.speaker))).filter(Boolean);

  const isReady = recording.status === 'ready' || recording.status === 'completed';
  const isPromoted = !!recording.promotedDocId;
  const showSpinner = showsSpinner(recording.status);
  const isFailed = recording.status === 'failed' || recording.status === 'error';
  const isCancelled = recording.status === STATUS_CANCELLED;
  // The three server-accepted cancel statuses, shared with RecordingList so the
  // two surfaces cannot drift (see CANCELLABLE_STATUSES in recordingsStore).
  const canCancel = canCancelTranscription(recording.status);
  // The row is deliberately non-optimistic, so `status` — and therefore
  // `canCancel` — does not move for the whole round-trip. This is the only
  // signal the click landed, and the only thing stopping a second request the
  // server refuses.
  const cancelInFlight = cancelPending.has(recording.recordingId);

  // ---- Title editing ----
  const [editingTitle, setEditingTitle] = React.useState<string | null>(null);
  const commitTitleRename = () => {
    if (editingTitle == null) return;
    const trimmed = editingTitle.trim();
    setEditingTitle(null);
    if (!trimmed || trimmed === recording.title) return;
    rename(recording.recordingId, { title: trimmed });
  };
  const cancelTitleRename = () => setEditingTitle(null);

  // Metadata block: createdAt = upload time, updatedAt = last mutation
  // (transcription completion or user edit). Display labels are intentionally
  // approximate ("Recorded" / "Updated") because the orchestrator's last
  // action on a row is typically the transcribe-success update.
  const recordedAt = recording.createdAt
    ? new Date(Number(recording.createdAt.seconds) * 1000)
    : null;
  const updatedAt = recording.updatedAt
    ? new Date(Number(recording.updatedAt.seconds) * 1000)
    : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editingTitle != null ? (
            <Input
              autoFocus
              value={editingTitle}
              onChange={(e) => setEditingTitle(e.target.value)}
              onBlur={commitTitleRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTitleRename();
                else if (e.key === 'Escape') cancelTitleRename();
              }}
              className="font-serif text-xl"
              aria-label={t('recordingPlayer.editTitleLabel')}
              maxLength={200}
            />
          ) : (
            <button
              type="button"
              className="group inline-flex items-center gap-2 max-w-full text-left"
              onClick={() => setEditingTitle(recording.title || recording.originalName || '')}
              aria-label={t('recordingPlayer.renameRecordingLabel')}
            >
              <h2 className="font-serif text-xl font-medium text-foreground truncate">
                {recording.title || recording.originalName || t('recordingPlayer.untitledRecording')}
              </h2>
              <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
            </button>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            <span>{formatDuration(recording.durationSecs)}</span>
            {/* Self-hosted ASR is free unless ops sets a per-minute rate, so
                cost is 0 for most recordings. Omit it then rather than show
                "≈ 0 credits". */}
            {Number(recording.costCredits) > 0 && (
              <>
                <span aria-hidden> · </span>
                <span title={t('recordingPlayer.approxCostTitle')}>
                  {t('recordingPlayer.approxCost', {
                    cost: formatCreditCost(Number(recording.costCredits), locale),
                  })}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showSpinner && (
            <Badge variant="running">
              <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden />
              {statusBadgeLabel(recording.status, t)}
            </Badge>
          )}
          {isFailed && (
            <Badge variant="failed">
              <FailedReasonText reason={recording.failureReason} fallback={t('recordingPlayer.statusFailed')} />
            </Badge>
          )}
          {isReady && <Badge variant="completed">{t('recordingPlayer.statusReady')}</Badge>}
          {/* Neutral `queued` (bg-muted/text-muted-foreground), NOT `paused` —
              that variant is byte-identical to `warning` and reads as "something
              is wrong". A cancel is a deliberate user action. */}
          {isCancelled && (
            <Badge variant="queued">{t('recordingPlayer.statusCancelled')}</Badge>
          )}
          {canCancel && (
            <Button
              variant="ghost"
              size="sm"
              disabled={cancelInFlight}
              onClick={() => cancelTranscription(recording.recordingId)}
              aria-label={t('recordingPlayer.cancelTranscriptionLabel')}
            >
              {cancelInFlight ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
              ) : (
                <XCircle className="h-3.5 w-3.5 mr-1" />
              )}{' '}
              {t('recordingPlayer.cancel')}
            </Button>
          )}
          {(isFailed || isCancelled) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => retry(recording.recordingId)}
              disabled={asrLocked}
              aria-label={t('recordingPlayer.retryTranscriptionLabel')}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> {t('recordingPlayer.retry')}
            </Button>
          )}
          {isReady && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => retry(recording.recordingId)}
              disabled={asrLocked}
              aria-label={t('recordingPlayer.reTranscribeLabel')}
              title={t('recordingPlayer.reTranscribeTitle')}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> {t('recordingPlayer.reTranscribe')}
            </Button>
          )}
          {isReady && !isPromoted && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => promote(recording.recordingId)}
              aria-label={t('recordingPlayer.addToDocumentsLabel')}
            >
              <FileText className="h-3.5 w-3.5 mr-1" /> {t('recordingPlayer.addToDocuments')}
            </Button>
          )}
          {isPromoted && (
            <Link
              href={`/library?id=${encodeURIComponent(recording.promotedDocId)}`}
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> {t('recordingPlayer.inLibrary')}
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => destroy(recording.recordingId)}
            aria-label={t('recordingPlayer.deleteLabel')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t('recordingPlayer.close')}
            </Button>
          )}
        </div>
      </div>

      <TranscriptionPlanUpsell
        show={asrLocked && (isFailed || isCancelled || isReady)}
        requiredTier={asr.requiredTier}
      />

      {/* Manual workflow actions bound to the transcript surface — e.g. the
          rich-note / extract-action-items workflow. Server resolves the
          transcript under the session DEK from this recording id; renders
          nothing when no actions are bound (feature off / no triggers). */}
      {isReady && (
        <ManualActionBar
          surface="recording-transcript"
          objectId={recording.recordingId}
          className="empty:hidden"
        />
      )}

      {/* Metadata — preserves origin info regardless of how the user renames */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-3 lg:grid-cols-4">
        {recordedAt && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaRecorded')}</dt>
            <dd>{recordedAt.toLocaleString()}</dd>
          </div>
        )}
        {updatedAt && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaUpdated')}</dt>
            <dd>{updatedAt.toLocaleString()}</dd>
          </div>
        )}
        {recording.mime && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaFormat')}</dt>
            <dd className="truncate" title={recording.mime}>{recording.mime}</dd>
          </div>
        )}
        {recording.source && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaSource')}</dt>
            <dd className="truncate">
              {recording.source}
              {recording.sourceUrl ? (
                <>
                  {' · '}
                  <a
                    href={recording.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    {t('recordingPlayer.sourceLink')}
                  </a>
                </>
              ) : null}
            </dd>
          </div>
        )}
        {recording.originalName && recording.originalName !== recording.title && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaOriginalName')}</dt>
            <dd className="truncate" title={recording.originalName}>{recording.originalName}</dd>
          </div>
        )}
        {recording.asrModel && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaModel')}</dt>
            <dd className="truncate" title={recording.asrModel}>{recording.asrModel}</dd>
          </div>
        )}
        {recording.transcript?.language && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaLanguage')}</dt>
            <dd>{recording.transcript.language}</dd>
          </div>
        )}
        {recording.sizeBytes > 0 && (
          <div>
            <dt className="font-medium text-foreground/70">{t('recordingPlayer.metaSize')}</dt>
            <dd>
              {t('recordingPlayer.sizeKb', {
                size: (Number(recording.sizeBytes) / 1024).toFixed(0),
              })}
            </dd>
          </div>
        )}
      </dl>

      {/* R22 agentic ambient capture opt-in. Per-recording consent: when on,
          opted-in workflows act on this transcript (extract todos, run research)
          once it's ready. Hidden entirely unless the feature flag is on. The
          toggle is intentionally an explicit, reversible affordance — never a
          default — because acting on a transcript may touch sensitive content. */}
      {agenticCaptureEnabled && (
        <div className="rounded-lg border border-border bg-surface/50 p-3" data-testid="agentic-capture-optin">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-border accent-accent"
              checked={recording.autoActOptIn ?? false}
              onChange={(e) => rename(recording.recordingId, { autoActOptIn: e.target.checked })}
              aria-label={t('recordingPlayer.agenticAriaLabel')}
            />
            <span className="text-xs">
              <span className="flex items-center gap-1 font-medium text-foreground">
                <Sparkles className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
                {t('recordingPlayer.agenticTitle')}
              </span>
              <span className="mt-0.5 block text-muted-foreground">
                {t('recordingPlayer.agenticDescription')}
              </span>
            </span>
          </label>
          {recording.autoActWorkflows && recording.autoActWorkflows.length > 0 && (
            <p className="mt-2 pl-6 text-xs text-muted-foreground" data-testid="agentic-capture-linkback">
              {t.rich('recordingPlayer.actedOnBy', {
                count: recording.autoActWorkflows.length,
                names: recording.autoActWorkflows.join(', '),
                strong: (chunks) => (
                  <span className="font-medium text-foreground">{chunks}</span>
                ),
              })}
            </p>
          )}
        </div>
      )}

      {/* Notes that link back to this recording. Lazy-loaded on mount via
          notes:listByLink; hidden entirely when there are no matches so it
          never adds visual noise to recordings without notes. */}
      <NotesFromRecording recordingId={recording.recordingId} />

      {/* Speaker chips — rename affordance + self-recognition badge/control */}
      <SpeakerChips
        speakers={speakers}
        recordingId={recording.recordingId}
        rename={rename}
        selfLabel={selfLabel}
        displayName={displayName}
        selfControlsEnabled={speakerRecognitionEnabled && isReady && segments.length > 0}
        setSelfSpeaker={setSelfSpeaker}
      />

      {/* Audio — deliberately NOT gated on transcription. The recording's own
          bytes are the thing the user gave us; losing access to them because a
          downstream ASR step failed is the worst possible failure mode. Hidden
          only while the upload itself is still in flight. */}
      {recording.status !== 'uploading' && recording.status !== 'pending' && (
        <AudioSection
          audioRef={audioRef}
          audioState={audioState}
          audioError={audioError}
          audioSrc={audioSrc}
          status={recording.status}
          title={recording.title}
          mime={recording.mime}
          onTimeUpdate={onTimeUpdate}
        />
      )}

      {/* Transcript */}
      <div>
        {showSpinner && <TranscribingProgress recording={recording} />}
        {isFailed && (
          <p className="text-sm text-destructive">
            {/* BILL-05: the top-up link lives here only — not inside the badge. */}
            <FailedReasonText
              reason={recording.failureReason}
              fallback={t('recordingPlayer.transcriptionFailed')}
              withTopUp
            />
          </p>
        )}
        {/* Without this branch `cancelled` matches none of showSpinner /
            isFailed / isReady and the whole transcript region renders empty —
            no copy, no next step, on the exact screen the user lands on right
            after clicking Cancel. Muted, NOT destructive: a cancel is a
            deliberate user action, matching the neutral badge above. */}
        {isCancelled && (
          <p className="text-sm text-muted-foreground">
            {t('recordingPlayer.transcriptionCancelled')}
          </p>
        )}
        {isReady && transcriptError && (
          <p className="text-sm text-destructive">{transcriptError}</p>
        )}
        {isReady && !transcriptError && segments.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('recordingPlayer.noTranscriptTurns')}</p>
        )}
        {isReady && segments.length > 0 && (
          <TranscriptList
            segments={segments}
            activeIdx={activeIdx}
            query={query}
            onSeek={seekTo}
            selfLabel={selfLabel}
            selfDisplayName={displayName}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The clickable, timestamped transcript turns. Extracted from `RecordingPlayer`
 * so the player's render stays under the cyclomatic-complexity budget and so
 * the search-match logic (highlight + count + jump-to-first-match) lives in one
 * focused unit.
 */
function TranscriptList({
  segments,
  activeIdx,
  query,
  onSeek,
  selfLabel,
  selfDisplayName,
}: {
  segments: readonly TranscriptSegment[];
  activeIdx: number;
  query?: string | undefined;
  onSeek: (secs: number) => void;
  /** Cluster label resolved to the account holder, or "" when none. */
  selfLabel: string;
  /** The account holder's login display name (rendered for self turns). */
  selfDisplayName: string;
}) {
  const t = useTranslations('recordings');
  const firstMatchRef = React.useRef<HTMLLIElement | null>(null);
  const matchQuery = query?.trim().toLowerCase() ?? '';

  const { firstMatchIdx, matchCount } = React.useMemo(() => {
    if (!matchQuery) return { firstMatchIdx: -1, matchCount: 0 };
    let first = -1;
    let count = 0;
    segments.forEach((s, i) => {
      if (s.text.toLowerCase().includes(matchQuery)) {
        if (first === -1) first = i;
        count += 1;
      }
    });
    return { firstMatchIdx: first, matchCount: count };
  }, [segments, matchQuery]);

  // Bring the first matching turn into view so the highlight isn't stranded
  // off-screen below the audio/metadata when a search is active.
  React.useEffect(() => {
    if (firstMatchIdx >= 0) firstMatchRef.current?.scrollIntoView({ block: 'nearest' });
  }, [firstMatchIdx]);

  return (
    <>
      {matchQuery && (
        <p className="mb-1 text-xs text-muted-foreground" data-testid="transcript-match-count">
          {matchCount === 0
            ? t('transcriptList.noMatch', { query: matchQuery })
            : t('transcriptList.matchCount', { count: matchCount, query: matchQuery })}
        </p>
      )}
      <ol className="space-y-1" data-testid="transcript-list">
        {segments.map((seg, idx) => (
          <li key={`${idx}-${seg.startSecs}`} ref={idx === firstMatchIdx ? firstMatchRef : undefined}>
            <button
              type="button"
              onClick={() => onSeek(seg.startSecs)}
              data-active={activeIdx === idx ? 'true' : undefined}
              className={cn(
                'group flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors duration-150',
                activeIdx === idx ? 'bg-accent/10 text-foreground' : 'hover:bg-hover text-foreground',
              )}
              aria-current={activeIdx === idx ? 'true' : undefined}
            >
              <span className="font-mono text-[10px] text-muted-foreground tabular-nums shrink-0 mt-0.5">
                {formatTimestamp(seg.startSecs)}
              </span>
              <span className="text-xs font-medium text-muted-foreground shrink-0 inline-flex items-center">
                {selfLabel && seg.speaker === selfLabel ? (
                  // Self turn: show the login display name + the "you · auto"
                  // badge in place of the raw cluster label. The label itself is
                  // never search-highlighted here because what renders is the
                  // resolved name, not the literal speaker string — but the turn
                  // text below still highlights normally.
                  <>
                    <span>{selfDisplayName}</span>
                    <SelfBadge />
                  </>
                ) : (
                  <Highlight text={seg.speaker || '—'} query={matchQuery} />
                )}
              </span>
              <span className="text-sm leading-snug">
                <Highlight text={seg.text} query={matchQuery} />
              </span>
            </button>
          </li>
        ))}
      </ol>
    </>
  );
}

/**
 * Lists every note in the user's store that links back to this recording —
 * the reverse view of the link the recording-to-note workflows write at the
 * end of their run. Fires `notes:listByLink` on mount with a per-mount
 * AbortController to swallow races when the user switches recordings before
 * the request settles. Hidden entirely when there are no matches.
 */
function NotesFromRecording({ recordingId }: { recordingId: string }) {
  const t = useTranslations('recordings');
  const listByLink = useNotesStore((s) => s.actions.listByLink);
  const [notes, setNotes] = React.useState<{ id: string; title: string }[] | null>(null);

  React.useEffect(() => {
    if (!recordingId) return;
    const ac = new AbortController();
    listByLink('recording', recordingId, { signal: ac.signal }).then(
      (rows) => {
        if (!ac.signal.aborted) setNotes(rows);
      },
      (err: unknown) => {
        // Silent on failure — the panel is a secondary affordance; surfacing
        // a toast here would be more noise than signal. The user still has
        // the full notes list and the workflow link-back in the doc itself.
        // AbortError (raised when the parent unmounts / recordingId changes
        // before the response arrives) is expected and must not flip state.
        const aborted = (err as { name?: string })?.name === 'AbortError' || ac.signal.aborted;
        if (!aborted) setNotes([]);
      },
    );
    return () => {
      ac.abort();
    };
  }, [recordingId, listByLink]);

  if (notes === null || notes.length === 0) return null;

  return (
    <section className="rounded-lg border border-border bg-surface/50 p-3" data-testid="notes-from-recording">
      <h4 className="text-xs font-medium text-foreground/80">
        {t('notesFromRecording.title', { count: notes.length })}
      </h4>
      <ul className="mt-2 space-y-1">
        {notes.map((n) => (
          <li key={n.id}>
            <a
              href={`/notes?id=${encodeURIComponent(n.id)}`}
              className="text-sm text-accent hover:underline"
            >
              {n.title || t('notesFromRecording.untitledNote')}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export { RecordingPlayer };
